import { useState } from 'react';
import useAuthStore from '../store/authStore';
import AcquireOSLogo from '../components/AcquireOSLogo';
import '../styles/authentication.css';

function AuthenticationPage({ onBack }) {
  const [formData, setFormData] = useState({
    fullName: '',
    email: ''
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [errors, setErrors] = useState({});
  const [isAdminView, setIsAdminView] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [showAdminPassword, setShowAdminPassword] = useState(false);

  const {
    loginWithEmail,
    verifyEmailCode,
    loginAsAdmin,
    isLoading,
    isVerifying,
    tempUser,
    lastEmailPreview
  } = useAuthStore();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!formData.email.trim()) {
      setErrors(prev => ({ ...prev, email: 'Admin email is required' }));
      return;
    }
    if (!adminPassword.trim()) {
      setErrors(prev => ({ ...prev, password: 'Password is required' }));
      return;
    }
    const result = await loginAsAdmin(formData.email, adminPassword);
    if (!result.success) setErrors({ general: result.error });
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (isAdminView) { handleAdminLogin(e); return; }
    if (validateForm()) {
      localStorage.setItem('aq_user_role', 'investor');
      const result = await loginWithEmail(formData);
      if (!result.success) setErrors({ general: result.error });
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setErrors({ verification: 'Verification code is required' });
      return;
    }
    const result = await verifyEmailCode(verificationCode);
    if (!result.success) setErrors({ verification: result.error });
  };

  const handleResendCode = async () => {
    if (tempUser) {
      const result = await loginWithEmail(formData);
      if (result.success) setErrors({ verification: 'New code sent to your email' });
    }
  };

  const handleBackToLogin = () => {
    setVerificationCode('');
    setErrors({});
  };

  const featureItems = [
    {
      label: 'AI Document Reading',
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 3h7l4 4v14H7z" />
          <path d="M14 3v5h5" />
          <path d="M10 17v-4" />
          <path d="M14 17V9" />
          <path d="M18 17v-6" />
        </svg>
      )
    },
    {
      label: 'Instant Deal Scoring',
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M13 2 4 14h7l-1 8 10-13h-7z" />
        </svg>
      )
    },
    {
      label: 'Smart Lender Matching',
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8.5 11.5 11 14a3 3 0 0 0 4.2 0l.8-.8" />
          <path d="M7 15.5 4.5 13a3 3 0 0 1 0-4.2l2.1-2.1a3 3 0 0 1 4.2 0l1.1 1.1" />
          <path d="M13.1 7.8 14.2 6.7a3 3 0 0 1 4.2 0l1.1 1.1a3 3 0 0 1 0 4.2L16 15.5" />
          <path d="M8 12.5 6.5 14" />
        </svg>
      )
    },
    {
      label: 'Auto LOI Generation',
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v4" />
          <path d="M12 18v4" />
          <path d="M2 12h4" />
          <path d="M18 12h4" />
          <path d="m4.9 4.9 2.8 2.8" />
          <path d="m16.3 16.3 2.8 2.8" />
          <path d="m19.1 4.9-2.8 2.8" />
          <path d="m7.7 16.3-2.8 2.8" />
        </svg>
      )
    },
    {
      label: 'Financial Calculator',
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="5" y="3" width="14" height="18" rx="2" />
          <path d="M8 7h8" />
          <path d="M8 11h2" />
          <path d="M12 11h2" />
          <path d="M16 11h.01" />
          <path d="M8 15h2" />
          <path d="M12 15h2" />
          <path d="M16 15h.01" />
        </svg>
      )
    },
    {
      label: 'Deal Pipeline Tracker',
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 19h16" />
          <path d="M5 17l4-5 4 3 5-8" />
          <circle cx="9" cy="12" r="1" />
          <circle cx="13" cy="15" r="1" />
          <circle cx="18" cy="7" r="1" />
        </svg>
      )
    }
  ];

  /* ── Verification screen ─────────────────────────────────── */
  if (isVerifying) {
    return (
      <div className="aq-auth-root aq-verify-bg">
        <div className="aq-verify-card">
          <button className="aq-verify-back" onClick={handleBackToLogin}>
            ← Back
          </button>
          <div className="aq-verify-logo">
            <AcquireOSLogo size={64} />
          </div>
          <h1 className="aq-verify-title">Verify Your Email</h1>
          <p className="aq-verify-sub">We've sent a 6-digit verification code to:</p>
          <div className="aq-verify-email">{tempUser?.email}</div>

          {/* OTP code is logged to browser console in DEV mode */}

          {errors.verification && (
            <div className="aq-auth-error">{errors.verification}</div>
          )}

          <form onSubmit={handleVerification} style={{ width: '100%' }}>
            <div className="aq-code-wrap">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="aq-code-input"
                placeholder="000000"
                maxLength={6}
              />
            </div>
            <button
              type="submit"
              className="aq-auth-btn aq-auth-btn--primary"
              disabled={isLoading || verificationCode.length !== 6}
            >
              {isLoading ? 'Verifying...' : 'Verify Code →'}
            </button>
          </form>

          <div className="aq-verify-options">
            <p>Didn't receive the code?</p>
            <button
              type="button"
              className="aq-link-btn"
              onClick={handleResendCode}
              disabled={isLoading}
            >
              Resend Code
            </button>
          </div>

          <div className="aq-verify-info">
            <div className="aq-verify-info-item">
              <svg className="aq-info-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
              <span>Secure 2-step verification</span>
            </div>
            <div className="aq-verify-info-item">
              <svg className="aq-info-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
              <span>Code expires in 30 minutes</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Main login screen ──────────────────────────────────── */
  return (
    <div className="aq-auth-root">

      {/* Left panel — blue brand */}
      <div className="aq-auth-left">
        <div className="aq-auth-left-inner">

          <div className="aq-auth-logo">
            <AcquireOSLogo size={104} onDark />
            <p className="aq-platform-subtext">Multifamily Deal Intelligence Platform</p>

          </div>

          <div className="aq-auth-features">
            <div className="aq-left-sec-hdr">Integrated Cognitive Modules</div>
            <div className="aq-feature-grid">
              {featureItems.map((feature) => (
                <div className="aq-feature-item" key={feature.label}>
                  <span className="aq-feature-icon">{feature.icon}</span>
                  <span className="aq-feature-label">{feature.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="aq-auth-stats">
            <div className="aq-left-sec-hdr" style={{ marginTop: '8px' }}>Real-time Platform Activity</div>
            <div className="aq-stats-row">
              <div className="aq-stat">
                <span className="aq-stat-num">247</span>
                <span className="aq-stat-desc">Active Deals</span>
              </div>
              <div className="aq-stat">
                <span className="aq-stat-num">$1.2B</span>
                <span className="aq-stat-desc">Total Volume</span>
              </div>
              <div className="aq-stat">
                <span className="aq-stat-num">39.5%</span>
                <span className="aq-stat-desc">Avg. IRR</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Right panel — form */}
      <div className="aq-auth-right">
        <div className="aq-auth-form-wrap">

          {onBack && !isAdminView && (
            <button
              type="button"
              className="aq-back-btn"
              onClick={onBack}
            >
              ← Back to Landing
            </button>
          )}

          <h2 className="aq-auth-form-title">
            {isAdminView ? 'Administrator Portal' : 'Sign in to your account'}
          </h2>
          <p className="aq-auth-form-sub">
            {isAdminView
              ? 'Enter your credentials to manage user activity and validate deals.'
              : "Welcome back! Let's close your next deal."}
          </p>

          {errors.general && (
            <div className="aq-auth-error">{errors.general}</div>
          )}

          <form onSubmit={handleEmailLogin} className="aq-auth-form">
            {!isAdminView ? (
              <>
                <div className="aq-field">
                  <label className="aq-label" htmlFor="email">Email Address</label>
                  <input
                    type="text"
                    id="email"
                    name="email"
                    inputMode="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`aq-input${errors.email ? ' aq-input--error' : ''}`}
                    placeholder="Enter your email"
                  />
                  {errors.email && <span className="aq-field-error">{errors.email}</span>}
                </div>

                <button
                  type="submit"
                  className="aq-auth-btn aq-auth-btn--primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending code...' : 'Email me a code'}
                </button>

                <div className="aq-auth-switch">
                  <a
                    href="#"
                    id="admin-login-link"
                    className="aq-switch-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsAdminView(true);
                      setFormData({ fullName: 'Admin User', email: 'admin@acquireos.com' });
                      setAdminPassword('');
                      setErrors({});
                    }}
                  >
                    Are you a platform administrator? Sign in here.
                  </a>
                </div>
              </>
            ) : (
              <>
                <div className="aq-field">
                  <label className="aq-label" htmlFor="email">Administrator Email</label>
                  <input
                    type="text"
                    id="email"
                    name="email"
                    inputMode="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`aq-input${errors.email ? ' aq-input--error' : ''}`}
                    placeholder="admin@acquireos.com"
                  />
                  {errors.email && <span className="aq-field-error">{errors.email}</span>}
                </div>

                <div className="aq-field">
                  <label className="aq-label" htmlFor="password">Security Password</label>
                  <div className="aq-password-wrapper">
                    <input
                      type={showAdminPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={adminPassword}
                      onChange={(e) => {
                        setAdminPassword(e.target.value);
                        if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
                      }}
                      className={`aq-input aq-input-password${errors.password ? ' aq-input--error' : ''}`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="aq-password-toggle"
                      onClick={() => setShowAdminPassword(!showAdminPassword)}
                      aria-label={showAdminPassword ? 'Hide password' : 'Show password'}
                    >
                      {showAdminPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {errors.password && <span className="aq-field-error">{errors.password}</span>}
                </div>

                <button
                  type="submit"
                  className="aq-auth-btn aq-auth-btn--primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Authenticating...' : 'Sign In as Admin →'}
                </button>

                <div className="aq-auth-switch">
                  <a
                    href="#"
                    id="investor-login-link"
                    className="aq-switch-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsAdminView(false);
                      setFormData({ fullName: '', email: '' });
                      setAdminPassword('');
                      setErrors({});
                    }}
                  >
                    ← Back to standard user login
                  </a>
                </div>
              </>
            )}
          </form>

        </div>
      </div>

    </div>
  );
}

export default AuthenticationPage;
